import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { WARRANTY_EXCEPTIONS_URL, WARRANTY_PARTNER_POLICIES_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import { getHealth } from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDateTime } from "../common/format";
import { isPartnerSignal, signalLabel, signalSentence } from "../common/opsLabels";
import "../warranty.css";

export const WINDOWS = [
  { value: "1h", label: "1 hour", phrase: "the last hour" },
  { value: "24h", label: "24 hours", phrase: "the last 24 hours" },
  { value: "7d", label: "7 days", phrase: "the last 7 days" },
  { value: "30d", label: "30 days", phrase: "the last 30 days" },
];

export const windowPhrase = (value) =>
  WINDOWS.find((item) => item.value === value)?.phrase || value;

const HealthDashboard = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  const [windowKey, setWindowKey] = useState("24h");
  const [view, setView] = useState({ status: "loading", data: null, message: "", refreshing: false });
  const seq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The tiles stay on screen while a new window or a refresh loads, so nothing jumps.
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setView((prev) => ({ ...prev, refreshing: true, status: prev.data ? "ready" : "loading" }));
    const result = await getHealth(accessToken, windowKey);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (result.ok) {
      setView({ status: "ready", data: result.data, message: "", refreshing: false });
    } else {
      setView((prev) => ({
        status: "error",
        data: prev.data,
        message: result.message,
        refreshing: false,
      }));
    }
  }, [accessToken, windowKey]);

  useEffect(() => {
    if (allowed) {
      load();
    }
  }, [allowed, load]);

  const data = view.data;

  return (
    <WarrantyPage
      title="Warranty health"
      sub={
        data
          ? `Market ${data.country}. Updated ${formatDateTime(data.generatedAt)}.`
          : "Market IN."
      }
      allowed={allowed}
      deniedTitle="You do not have access to warranty health"
      deniedText="Ask an admin to give you the Warranty Operations permission."
      actions={
        <>
          <div className="warranty-window-picker" role="group" aria-label="Time window">
            {WINDOWS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`warranty-inline-btn${windowKey === item.value ? " is-primary is-selected" : ""}`}
                aria-pressed={windowKey === item.value}
                onClick={() => setWindowKey(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <Button className="Addbtn" onClick={load} disabled={view.refreshing}>
            {view.refreshing ? "Refreshing" : "Refresh"}
          </Button>
        </>
      }
    >
      {view.status === "loading" && !data && <Loading label="Loading warranty health" />}
      {view.status === "error" && <ErrorState message={view.message} onRetry={load} retrying={view.refreshing} />}
      {data && view.status !== "error" && data.signals.length === 0 && (
        <EmptyState title="No signals are switched on">
          <span>Warranty health has nothing to show for this market yet.</span>
        </EmptyState>
      )}
      {data && view.status !== "error" && data.signals.length > 0 && (
        <div className="warranty-tile-grid" aria-busy={view.refreshing}>
          {data.signals.map((signal) => (
            <Link
              key={signal.signal}
              className={`warranty-tile${signal.count === 0 ? " is-zero" : ""}`}
              to={
                isPartnerSignal(signal.signal)
                  ? `${WARRANTY_PARTNER_POLICIES_URL}?tab=${encodeURIComponent(signal.signal)}`
                  : `${WARRANTY_EXCEPTIONS_URL}/${encodeURIComponent(signal.signal)}?window=${encodeURIComponent(
                      data.window
                    )}`
              }
              aria-label={`${signalLabel(signal.signal)}: ${signal.count}. Open the list.`}
            >
              <span className="warranty-tile-label">{signalLabel(signal.signal)}</span>
              <span className="warranty-tile-count">{signal.count}</span>
              <span className="warranty-tile-new">
                {signal.kind === "event"
                  ? `in ${windowPhrase(data.window)}`
                  : `${signal.inWindow} new in ${windowPhrase(data.window)}`}
              </span>
              <span className="warranty-tile-sentence">{signalSentence(signal.signal)}</span>
            </Link>
          ))}
        </div>
      )}
    </WarrantyPage>
  );
};

export default HealthDashboard;
